/* @flow */

import type { RootState, Action } from "../types";
import { combineReducers } from "redux";
import { isDDARequired } from "../countryHelper";
import contactsReducer from "./contacts";
import ddaReducer from "./dda";
import businessReducer from "./business";
import processingReducer from "./processing";
import lifecycleReducer from "./lifecycle";
import fundingReducer from "./funding";
import uiReducer from "./ui";
import disclaimersReducer from "./disclaimers";
import paypalDetailsReducer from "./paypalDetails";
import midTypesReducer from "./midTypes";
import additionalFundingReducer from "./additionalFunding";
import { hydrateStore } from "../dev/hydrateStore";
import sharedValidationSchema from "../validators/schemas/shared";
import { ValidationError } from "yup";
import { getOpportunityId } from "../dom";

const combinedReducer = (
  countryCode: string,
  countries: Array<string>,
  venmoOnly: boolean
): ((RootState | void, Action) => RootState) =>
  combineReducers({
    business: businessReducer(countryCode, venmoOnly),
    contacts: contactsReducer(countryCode),
    dda: ddaReducer(),
    funding: fundingReducer(countryCode),
    lifecycle: lifecycleReducer,
    processing: processingReducer(countryCode),
    disclaimers: disclaimersReducer,
    ui: uiReducer,
    countryCode: (state = countryCode) => state || "",
    countries: (state = countries) => state || [],
    paypalDetails: paypalDetailsReducer(countryCode),
    additionalFunding: additionalFundingReducer(countryCode),
    venmoOnly: (state = venmoOnly) => state || false,
    opportunityId: (state: string = getOpportunityId()) => state,
    midTypes: midTypesReducer(countryCode)
  });

const validateShared = (state: RootState): string | void => {
  try {
    sharedValidationSchema.validateSync(
      {
        registered_as: state.business.values?.registered_as,
        beneficial_owners: [
          ...state.contacts.values?.map(c => c?.ownership?.beneficial_owner)
        ]
      },
      { context: { countryCode: state.countryCode } }
    );
  } catch (error) {
    if (ValidationError.isError(error)) {
      return error.message;
    }

    throw error;
  }
  return undefined;
};

const reducer = (
  countryCode: string,
  countries: Array<string>,
  venmoOnly: boolean
) => (state: RootState | void, action: Action) => {
  let nextState = state;

  if (action.type === "HYDRATE") {
    nextState = hydrateStore(countryCode, venmoOnly);
  }

  const newState = combinedReducer(countryCode, countries, venmoOnly)(
    nextState,
    action
  );

  const isDDARequiredForCountry = isDDARequired(countryCode);
  const no_dda_errors = newState.dda.errorText === undefined;

  const sharedError = validateShared(newState);

  const isSubmittable = isDDARequiredForCountry ?
    newState.contacts.errorText === undefined && no_dda_errors && sharedError === undefined :
    newState.contacts.errorText === undefined && sharedError === undefined;

  return {
    ...newState,
    lifecycle: {
      ...newState.lifecycle,
      isSubmittable,
      sharedError,
      isDDARequired: isDDARequiredForCountry
    }
  };
};

export default reducer;
